import { z } from 'zod';
import { OrganizationRole, organizationRoleSchema } from './organizations';
import { userSchema } from './user';
/*
  OrganizationUserStatus defines the available statuses for a user in an organization.
  Similar to Descope user status (invited/active/disabled).
*/
export var OrganizationUserStatus;
(function (OrganizationUserStatus) {
    OrganizationUserStatus["Active"] = "active";
    OrganizationUserStatus["Invited"] = "invited";
    OrganizationUserStatus["Disabled"] = "disabled";
})(OrganizationUserStatus || (OrganizationUserStatus = {}));
/*
  organizationUserStatusSchema is the Zod schema for validating organization user status.
*/
export const organizationUserStatusSchema = z.enum(OrganizationUserStatus);
/*
  DeploymentStatus indicates whether the product has been deployed to this user.
  Based on whether the user has an active API key for the organization.
  - Active: User has an active API key (extension/agent deployed)
  - Invited: User does not have an active API key yet
*/
export var DeploymentStatus;
(function (DeploymentStatus) {
    DeploymentStatus["Active"] = "active";
    // Generated: user has an active org API key but no client activity yet
    // (key generated, extension has not communicated). Sits between Active and
    // Invited (KIR-3786).
    DeploymentStatus["Generated"] = "generated";
    DeploymentStatus["Invited"] = "invited";
    // Future: Deactivated = 'deactivated',
})(DeploymentStatus || (DeploymentStatus = {}));
export const deploymentStatusSchema = z.nativeEnum(DeploymentStatus);
/*
  linkedGithubSchema defines the structure for a linked GitHub account.
  Included in organization member responses when the user has linked their GitHub account.
*/
export const linkedGithubSchema = z.object({
    login: z.string(),
    avatarUrl: z.string().nullish(),
});
/*
  linkedBitbucketSchema defines the structure for a linked Bitbucket account.
  Included in organization member responses when the user has linked their Bitbucket account.
*/
export const linkedBitbucketSchema = z.object({
    displayName: z.string(),
    nickname: z.string(),
    avatarUrl: z.string().nullish(),
});
/*
  organizationMemberSchema defines the structure for an organization member
  as returned by the list members endpoint.
  Extends the base userSchema with organization-specific fields.
  Includes authStatus field derived from Descope authentication state (active/invited/disabled).
  Includes deploymentStatus field derived from API key presence (active/invited).
*/
export const organizationMemberSchema = userSchema.extend({
    roles: z.array(organizationRoleSchema),
    authStatus: organizationUserStatusSchema.optional(),
    deploymentStatus: deploymentStatusSchema.optional(),
    // hasPendingInvite (KIR-4040): true only for rows sourced from an
    // `organization_invites` row — i.e. a user explicitly invited via Add Member
    // who has not yet accepted. The Users screen gates its Resend action on this,
    // NOT on `authStatus === Invited`: a member added silently (MDM / the org
    // handshake path) carries no invite row yet can have Descope status `invited`,
    // so authStatus alone would offer Resend on rows with nothing to resend.
    hasPendingInvite: z.boolean().optional(),
    createdAt: z.coerce.date().optional(),
    linkedGithub: linkedGithubSchema.nullish(),
    linkedBitbucket: linkedBitbucketSchema.nullish(),
    // KIR-3908: populated for `discovered`/`unattributed` rows (from base
    // userSchema.userType). Derived from the user's findings — first/last activity
    // and how many findings are attributed to them. Absent for full members.
    firstSeen: z.coerce.date().optional(),
    lastSeen: z.coerce.date().optional(),
    findingsCount: z.number().int().nonnegative().optional(),
    // KIR-5410 (shared devices): set only on rows for people offboarded from this
    // org (removed or IdP-offboarded, not re-added). They have no seat and no
    // role; the Users screen labels them Offboarded. Absent for everyone else.
    offboardedAt: z.coerce.date().optional(),
    // KIR-5410: where a Discovered/Unattributed row was seen — an OS sign-in on
    // an enrolled endpoint, or Cowork. Absent while shared devices is off.
    source: z.enum(['endpoint', 'cowork']).optional(),
});
/*
  invitedMemberSchema (KIR-3786): the invite-mutating endpoint responses. It
  extends a member row with the opaque `inviteToken` (a 256-bit link token, not
  the invite row id) so the owner who created/resent the invite can build the
  `?invite_token=` deep link. Returned by POST /organizations/:orgId/members and
  the resend route (POST .../members/:userId/resend) — the members LIST never
  includes the token.
*/
export const invitedMemberSchema = organizationMemberSchema.extend({
    inviteToken: z.string().optional(),
});
/*
  organizationMemberListSchema is the schema for listing organization members.
*/
export const organizationMemberListSchema = z.array(organizationMemberSchema);
/*
  Query for the member-roster reads (GET /members, GET /members/personal-keys).
  `fresh=true` asks the backend to read the Descope roster now instead of its
  Redis roster cache (KIR-4887). The web app sends it in two places: the reload
  right after the admin's own membership change (so it never gets a roster read
  before the change) and the Users page Refresh control (so changes made
  outside Kirin show at once).
*/
export const memberRosterQuerySchema = z.object({
    fresh: z.enum(['true', 'false']).optional(),
});
/*
  personalKeyState (KIR-3786): whether a flagged member's personal API key was
  merely generated recently or actually used recently (within the 7-day window).
  - active: key was used (last_used_at) within the last 7 days
  - generated: key was created within the last 7 days but not recently used
*/
export var PersonalKeyState;
(function (PersonalKeyState) {
    PersonalKeyState["Active"] = "active";
    PersonalKeyState["Generated"] = "generated";
})(PersonalKeyState || (PersonalKeyState = {}));
export const personalKeyStateSchema = z.nativeEnum(PersonalKeyState);
/*
  personalKeyMemberSchema (KIR-3786): a member who is still using a personal API
  key (organizationId IS NULL) instead of an org key, whose key was created or
  used within the last 7 days. Owners see a badge flagging these members.
*/
export const personalKeyMemberSchema = userSchema.extend({
    roles: z.array(organizationRoleSchema),
    authStatus: organizationUserStatusSchema.optional(),
    personalKeyState: personalKeyStateSchema,
});
export const personalKeyMemberListSchema = z.array(personalKeyMemberSchema);
/*
  newOrganizationMemberSchema is the schema for adding a new member to an organization.
  Handles both existing users and non-existing users (creates invite).
  If user doesn't exist, Descope will create them with "Invited" status and send an email.
*/
export const newOrganizationMemberSchema = z.object({
    email: z.email(),
    role: organizationRoleSchema.optional().default(OrganizationRole.Member),
});
/*
  updateMemberRoleSchema is the schema for updating a member's role in an organization.
*/
export const updateMemberRoleSchema = z.object({
    role: organizationRoleSchema,
});
//# sourceMappingURL=organization-members.js.map